import styles from "./ChatBox.module.css";
import Icon from "@lib/common/components/Icon/Icon";
import type { PaperProps, TextareaProps } from "@mantine/core";
import { Button, Group, Paper, Stack, Textarea } from "@mantine/core";
import type { KeyboardEvent, Ref } from "react";
import { useCallback, useState } from "react";

export default function ChatBox({
  ref,
  onSubmit,
  onCancel,
  disabled = false,
  isStreaming = false,
  textareaProps = {},
  paperProps
}: {
  ref: Ref<HTMLTextAreaElement>;
  onSubmit: (message: string) => void;
  onCancel?: () => void;
  disabled?: boolean;
  isStreaming?: boolean;
  textareaProps?: TextareaProps;
  paperProps?: Partial<PaperProps>;
}) {
  const [input, setInput] = useState("");

  const submit = useCallback(() => {
    const message = input.trim();
    if (message.length === 0) {
      return;
    }
    onSubmit(message);
    setInput("");
  }, [input, onSubmit]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        if (isStreaming) {
          return;
        }
        submit();
      }
    },
    [isStreaming, submit]
  );

  const submitDisabled = disabled || input.trim().length === 0;

  return (
    <Paper
      classNames={{ root: styles.textareaPaperWrapper }}
      shadow="xxs"
      px="sm"
      pt={5}
      pb="xs"
      withBorder
      w="100%"
      radius="md"
      {...paperProps}
    >
      <Stack gap="xs" align="space-between" h="100%">
        <Textarea
          ref={ref}
          {...textareaProps}
          value={input}
          // Compose handlers to ensure both internal state and any external
          // onChange (e.g., typewriter animation control) are called
          onChange={e => {
            setInput(e.currentTarget.value);
            textareaProps.onChange?.(e);
          }}
          onKeyDown={handleKeyDown}
          size="md"
          autoFocus
          data-autofocus
          autosize
          minRows={1}
          maxRows={6}
          variant="unstyled"
          radius="md"
          fw={500}
        />

        <Group justify="flex-end" align="flex-end">
          {isStreaming ? (
            <Button color="gray" variant="light" size="xs" miw="unset" px="xs" onClick={onCancel}>
              <Icon name="stop" />
            </Button>
          ) : (
            <Button
              color="var(--mantine-primary-color-filled)"
              variant="filled"
              size="xs"
              miw="unset"
              px="xs"
              disabled={submitDisabled}
              onClick={submit}
            >
              <Icon name="arrow-up" />
            </Button>
          )}
        </Group>
      </Stack>
    </Paper>
  );
}
